import { ChevronLeft, ChevronRight, LogOut, User } from 'lucide-react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ForgeContextSwitcher } from '../../../forge/components/layout/ForgeContextSwitcher';
import { ThemeToggle } from '../../../forge/components/layout/ThemeToggle';
import { ForgeTheme } from '../../../forge/hooks/useForgeTheme';
import { EVAL_NAV_SECTIONS } from './evalNavigation';

interface EvalSidebarProps {
  theme: ForgeTheme;
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  toggleCollapse: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
  isMobile?: boolean;
  /** Mobile drawer: called after a navigation so the layout can close the drawer. */
  onMobileNavigate?: () => void;
}

export const EvalSidebar: React.FC<EvalSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  toggleCollapse,
  onDragStart,
  toggleTheme,
  currentPath,
  isMobile = false,
  onMobileNavigate,
}) => {
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  const effectiveCollapsed = isCollapsed && !isMobile;

  const isActive = (path: string) => {
    if (path === '/eval/runs') {
      return currentPath === '/eval/runs' || currentPath === '/eval/runs/';
    }
    return currentPath.startsWith(path);
  };

  const sidebarClasses = [
    'forge-sidebar',
    effectiveCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
    isMobile ? 'is-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={sidebarClasses}
      style={{ width: !isMobile && !effectiveCollapsed ? width : undefined }}
      aria-label="Eval navigation"
    >
      <nav className="sidebar-nav">
        {EVAL_NAV_SECTIONS.map((section) => (
          <div key={section.title} style={{ marginBottom: 16 }}>
            <div className="sidebar-section-label">{section.title}</div>
            {section.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                title={effectiveCollapsed ? item.label : undefined}
                onClick={onMobileNavigate}
              >
                {item.icon}
                <span className="sidebar-item-label">{item.label}</span>
              </Link>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-footer-utilities">
          <ForgeContextSwitcher variant={isMobile ? 'drawer' : 'sidebar'} />

          <ThemeToggle
            theme={theme}
            onToggle={toggleTheme}
            variant="sidebar"
            collapsed={effectiveCollapsed}
          />

          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              navigate('/forge/profile');
            }}
            title={effectiveCollapsed ? 'Profile' : undefined}
          >
            <User />
            <span className="sidebar-collapse-label">Profile</span>
          </button>

          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              logout();
              navigate('/stores');
            }}
            title={effectiveCollapsed ? 'Sign out' : undefined}
          >
            <LogOut />
            <span className="sidebar-collapse-label">Sign out</span>
          </button>
        </div>

        {!isMobile ? (
          <>
            <div className="sidebar-footer-divider" />

            <button
              className="sidebar-collapse-btn"
              onClick={toggleCollapse}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
              <span className="sidebar-collapse-label">Collapse</span>
            </button>
          </>
        ) : null}
      </div>

      {!isMobile ? <div className="sidebar-drag-handle" onMouseDown={onDragStart} /> : null}
    </aside>
  );
};
